import { useState } from "react";
import { useNotification } from "@context/NotificationContext";
import {
  useClaimBayHireRequest,
  useUnclaimBayHireRequest,
  useVerifyBayHirePayment,
} from "@features/bay-hire/hooks/useBayHireMutations";
import { getApiErrorDetail } from "@features/bay-hire/utils/getApiErrorDetail";
import { useTranslation } from "react-i18next";

/**
 * DFT-0027 (Slice B2): Operations lifecycle actions for the routed Request
 * Detail page. Relocates the direct claim/unclaim/verify-payment mutations
 * (previously on the operations-queue kebab, DFT-0034) and the dialog-open
 * state for pre-approve/reject/cancel/reject-payment/add-to-waiting-list.
 *
 * The dialog-open state uses a single boolean map (mirroring
 * LicenseDetails.tsx's `openDrawers`) so the detail component stays lean.
 *
 * CRITICAL (see DFT-0027 plan): claim/unclaim/verify mutations invalidate only
 * the `queue` query key, NOT the single-request key this detail page reads, so
 * every direct action awaits `refetch()` from `useBayHireRequestById` to reflect
 * the new task/status. Dialog actions do the same via their `onSuccess`.
 *
 * Verify-payment additionally needs `waitForStatusChange`: it "mirrors
 * preApprove's TM-complete pattern" (BayHireOperationsService#verifyBayHirePayment),
 * so completing the task only signals the workflow - PAYED -> APPROVED lands in
 * the domain afterwards, and a single refetch races it. Claim/unclaim keep the
 * plain refetch on purpose: they change task ownership, not request status, so
 * polling for a status change would always exhaust the retry budget first.
 */
export type BayHireDetailDrawerName =
  | "preApprove"
  // DFT-0084: EXEMPTED-only direct-approve action - a separate drawer from "preApprove" since it
  // never enters PRE_APPROVED and has its own (much smaller) form.
  | "approveExempted"
  | "reject"
  | "cancel"
  | "rejectPayment"
  | "addToWaitingList"
  | "receiptUpload"
  // DFT-0044 slice F (F4): read-only Details drawer for a REJECTED/CANCELLED request
  // (05_Port_License_details "Details" wireframe) - reason + any F3 supporting-document
  // attachment, OK button only.
  | "details"
  // DFT-0044 slice F (FE-38, a presumption - flag in the PR for design review): read-only summary
  // of a PRE_APPROVED request's own decision (Chargeable/Total Amount/HOD document/Remarks).
  | "preApprovedSummary";

interface UseBayHireDetailActionsResult {
  inFlight: boolean;
  openDrawers: Record<BayHireDetailDrawerName, boolean>;
  openDrawer: (name: BayHireDetailDrawerName) => void;
  closeDrawer: (name: BayHireDetailDrawerName) => void;
  handleClaim: () => Promise<void>;
  handleUnclaim: () => Promise<void>;
  handleVerifyPayment: () => Promise<void>;
}

export const useBayHireDetailActions = (
  requestId: string | undefined,
  refetch: () => Promise<unknown>,
  /** Defaults to a single refetch so callers without an async action keep working. */
  waitForStatusChange: () => Promise<unknown> = refetch,
): UseBayHireDetailActionsResult => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [inFlight, setInFlight] = useState(false);
  const [openDrawers, setOpenDrawers] = useState<Record<BayHireDetailDrawerName, boolean>>({
    preApprove: false,
    approveExempted: false,
    reject: false,
    cancel: false,
    rejectPayment: false,
    addToWaitingList: false,
    receiptUpload: false,
    details: false,
    preApprovedSummary: false,
  });

  const openDrawer = (name: BayHireDetailDrawerName) =>
    setOpenDrawers((prev) => ({ ...prev, [name]: true }));
  const closeDrawer = (name: BayHireDetailDrawerName) =>
    setOpenDrawers((prev) => ({ ...prev, [name]: false }));

  const claimMutation = useClaimBayHireRequest();
  const unclaimMutation = useUnclaimBayHireRequest();
  const verifyPaymentMutation = useVerifyBayHirePayment();

  const handleClaim = async () => {
    if (!requestId) return;
    setInFlight(true);
    try {
      await claimMutation.mutateAsync(requestId);
      await refetch();
    } catch (error) {
      // DFT-0077 (AD-5): prefer the backend's own explanation (e.g. "Request 116 is CANCELLED -
      // a terminal status carries no Operations ownership actions") over the generic message -
      // same rationale as ReasonDialog's identical catch block (DFT-0066 AD-6).
      notify({
        title: t("common.error", { defaultValue: "Error" }),
        description: getApiErrorDetail(error, t("bay-hire.claim-error")),
        type: "error",
      });
    } finally {
      setInFlight(false);
    }
  };

  const handleUnclaim = async () => {
    if (!requestId) return;
    setInFlight(true);
    try {
      await unclaimMutation.mutateAsync(requestId);
      await refetch();
    } catch (error) {
      notify({
        title: t("common.error", { defaultValue: "Error" }),
        description: getApiErrorDetail(error, t("bay-hire.unclaim-error")),
        type: "error",
      });
    } finally {
      setInFlight(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!requestId) return;
    setInFlight(true);
    try {
      await verifyPaymentMutation.mutateAsync(requestId);
      await waitForStatusChange();
    } catch (error) {
      notify({
        title: t("common.error", { defaultValue: "Error" }),
        description: getApiErrorDetail(error, t("bay-hire.verify-payment-error")),
        type: "error",
      });
    } finally {
      setInFlight(false);
    }
  };

  return {
    inFlight,
    openDrawers,
    openDrawer,
    closeDrawer,
    handleClaim,
    handleUnclaim,
    handleVerifyPayment,
  };
};
